import { Flex } from '@chakra-ui/react';
import capitalize from 'lodash-es/capitalize';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    format12Hour,
    formatStoreHours,
    getHoliday,
    isClosedDay,
} from '../../bff-api/utils/store-utils';
import { Text } from '../../ui-components';
import type { Store } from '../../bff-api/stores/model';
import type { Day } from '../../bff-api/stores/model/Day';

interface Props {
    id: string;
    store: Store;
    showPhone?: boolean;
}

export const StoreHours = ({
    id,
    store: { phone, c_storeHoursData },
    showPhone = false,
}: Props): React.ReactElement | null => {
    const intl = useIntl();

    if (!c_storeHoursData?.standardHours) {
        return null;
    }

    const { standardHours, holidayHours, closedDates } = c_storeHoursData;
    const days = Object.keys(standardHours);
    const closedLabel = intl.formatMessage({
        id: 'store.item.closed',
        defaultMessage: 'closed',
    });

    return (
        <Flex data-testid={id} direction="column" gap={2}>
            <Flex data-testid={`${id}-value`} direction="column" gap={0.5}>
                {days.map((d) => {
                    const dayLowerCase = d.toLowerCase();
                    const day = d as Day;
                    const isClosed = isClosedDay(day, closedDates);

                    let formattedHours = closedLabel;

                    let holiday;

                    if (!isClosed) {
                        holiday = getHoliday(day, holidayHours);

                        if (holiday) {
                            formattedHours = format12Hour(holiday.startTime, holiday.endTime);
                        } else {
                            formattedHours = formatStoreHours(standardHours, day);
                        }
                    }

                    const capitalizedDay = capitalize(dayLowerCase);

                    return (
                        <Flex
                            data-testid={`${id}-${dayLowerCase}`}
                            key={day}
                            justifyContent="space-between"
                        >
                            <Text
                                id={`${id}-${dayLowerCase}-label`}
                                size="small"
                                color="slt-pepper.1000"
                            >
                                {capitalizedDay}
                            </Text>

                            {holiday ? (
                                <Flex direction="column">
                                    <Text
                                        id={`${id}-${dayLowerCase}-value`}
                                        size="small"
                                        style="bold"
                                        align="end"
                                        color="slt-pepper.1000"
                                    >
                                        {formattedHours}
                                    </Text>

                                    <Text
                                        id={`${id}-${dayLowerCase}-holiday`}
                                        size="small"
                                        style="bold"
                                        align="end"
                                        color="slt-pepper.1000"
                                    >
                                        ({holiday.label})
                                    </Text>
                                </Flex>
                            ) : (
                                <Text
                                    id={`${id}-${dayLowerCase}-value`}
                                    size="small"
                                    style="bold"
                                    align="end"
                                    color="slt-pepper.1000"
                                >
                                    {formattedHours}
                                </Text>
                            )}
                        </Flex>
                    );
                })}
            </Flex>

            {showPhone && (
                <Text id={`${id}-phone`} size="small">
                    <FormattedMessage id="header.phone" defaultMessage="Phone" />
                    {': '}
                    {phone}
                </Text>
            )}
        </Flex>
    );
};

StoreHours.defaultProps = {
    showPhone: false,
};
